import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
} from "@mui/material";

const UserDialog = ({ open, onClose, onSave, business }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    address: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (business) {
      // Editing existing business
      setFormData({
        id: business.id || "", // Include the ID
        name: business.name || "",
        email: business.email || "",
        phone: business.phone || "",
        address: business.address || "",
        password: "", // Start blank for security
      });
    } else {
      // Creating a new business
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        password: "",
      });
    }
  }, [business]);

  const validateForm = () => {
    const errors = {};

    // Name
    if (!formData.name.trim()) {
      errors.name = "Name is required.";
    }

    // Email
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Valid email is required.";
    }

    // Password
    if (business) {
      // If editing: only validate if user actually entered something
      if (formData.password && formData.password.length < 8) {
        errors.password = "Password must be at least 8 characters.";
      }
    } else {
      // If creating new: password is required and must be >= 8 chars
      if (!formData.password || formData.password.length < 8) {
        errors.password = "Password must be at least 8 characters.";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleSave = () => {
    if (!validateForm()) {
      setShowError(true);
      return;
    }
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {business ? "Edit Business Owner" : "Add New Business Owner"}
      </DialogTitle>
      <DialogContent>
        {showError && (
          <Alert severity="error">Please fix the highlighted errors.</Alert>
        )}

        <TextField
          fullWidth
          margin="normal"
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          error={!!formErrors.name}
          helperText={formErrors.name}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          error={!!formErrors.email}
          helperText={formErrors.email}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Phone"
          name="phone"
          type="number"
          value={formData.phone}
          onChange={handleChange}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
        />

        {/* Show a Password field in both editing & creating modes */}
        <TextField
          fullWidth
          margin="normal"
          label="Password"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          // Required for new business, but optional for editing
          required={!business}
          error={!!formErrors.password}
          helperText={formErrors.password}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {business ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDialog;
